import { PaperClipIcon } from '@heroicons/react/24/outline';
import React, { useRef } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { FileAttachmentInput } from 'src/shared/components';
import { Question } from 'src/shared/types/question';

import { addToast, Radio, RadioGroup } from 'src/shared/components/ui';
import { ButtonV2 } from 'src/shared/components/ui';

import { useQuestionFiles } from './hooks/useQuestionFiles';
import { useSendQuestion } from './hooks/useSendQuestion';

interface SingleChoiceQuestionProps {
  question: Question;
  productId: string;
  questionId: number;
  productStage: string;
  isComplete: boolean;
  [key: string]: any;
}
const SingleChoiceQuestion: React.FC<SingleChoiceQuestionProps> = ({
  question,
  productId,
  questionId,
  productStage
}) => {
  const { handleSubmit, watch, control, register } = useForm({
    defaultValues: {
      choice: '',
      files: [] as any[]
    }
  });

  const files = watch('files');
  const comment = watch('choice');

  const questionMutation = useSendQuestion();
  const questionFilesMutation = useQuestionFiles();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isPending =
    questionMutation.isPending || questionFilesMutation.isPending;

  const {
    fields: fileFields,
    append: appendFile,
    remove: removeFile
  } = useFieldArray({
    control,
    name: 'files'
  });

  const onSubmitFiles = () => {
    const filesBody = new FormData();

    files.forEach((file, i) => {
      filesBody.append(`files[${i}]`, file?.file);
      filesBody.append(`captions[${i}]`, file?.caption);
    });

    questionFilesMutation.mutate(
      { body: filesBody, productId, questionId, stageId: productStage },
      {
        onSuccess: () => {
          addToast({
            color: 'success',
            title: 'Question has been successfully answered.'
          });
        }
      }
    );
  };

  const onSubmit = (data: any) => {
    const withFiles = data.files.length > 0;

    questionMutation.mutate(
      {
        answer: data.choice,
        productId,
        questionId,
        stageId: productStage,
        withFiles
      },
      {
        onSuccess: () => {
          if (withFiles) {
            onSubmitFiles();
          } else {
            addToast({
              color: 'success',
              title: 'Question has been successfully answered.'
            });
          }
        }
      }
    );
  };

  const renderFiles = () => {
    return fileFields.map((field, index) => {
      const file = watch(`files.${index}.file`);
      const caption = watch(`files.${index}.caption`);
      const preview = watch(`files.${index}.preview`);

      return (
        <FileAttachmentInput
          key={field.id}
          fileInputProps={register(`files.${index}.file`)}
          captionInputProps={register(`files.${index}.caption`)}
          fileValue={file}
          captionValue={caption}
          preview={preview}
          onRemove={() => removeFile(index)}
        />
      );
    });
  };

  return (
    <div className="flex gap-2 mt-1 flex-col">
      <div className="flex gap-2 items-center w-full">
        <form
          className="flex flex-col gap-5 w-full items-start"
          id={`single-question-form-${questionId}`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="choice"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                value={field.value}
                onValueChange={field.onChange}
                classNames={{
                  label: 'text-primary-text text-[16px]'
                }}
              >
                {question.options?.map((option) => (
                  <Radio key={option} value={option}>
                    {option}
                  </Radio>
                ))}
              </RadioGroup>
            )}
          />

          <div className="flex flex-wrap gap-2">{renderFiles()}</div>

          <div className="flex items-center gap-2">
            <ButtonV2
              form={`single-question-form-${questionId}`}
              type="submit"
              size="sm"
              color="primary"
              isLoading={isPending}
              isDisabled={!comment || isPending}
            >
              Send
            </ButtonV2>
            <div className="flex items-center gap-2 flex-grow">
              <input
                type="file"
                ref={fileInputRef}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = e.target.files;

                  if (files && files.length > 0) {
                    const newEntries = Array.from(files).map((file) => ({
                      file,
                      caption: '',
                      preview: file.type.startsWith('image/')
                        ? URL.createObjectURL(file)
                        : null
                    }));

                    appendFile(newEntries);
                    e.target.value = '';
                  }
                }}
              />
              <ButtonV2
                isDisabled={isPending}
                size="md"
                isIconOnly
                color="tertiary"
                onPress={() => fileInputRef.current?.click()}
              >
                <PaperClipIcon className="size-4" />
              </ButtonV2>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SingleChoiceQuestion;
